/**
 * useChatMessageLimit Hook
 * Tracks daily message limits for free tier users
 */

import { useState, useEffect, useCallback } from "react";
import { canSendMessage } from "../api/firestore/services/chatMessageLimitService";

/**
 * Hook to track chat message limits for free tier users
 * @param {string} userId - User ID
 * @param {boolean} isOpen - Whether the chat dialog is open
 * @returns {Object} { remaining, limit, isLimited, loading, refresh }
 */
export const useChatMessageLimit = (userId, isOpen) => {
	const [remaining, setRemaining] = useState(null);
	const [limit, setLimit] = useState(null);
	const [isLimited, setIsLimited] = useState(false);
	const [loading, setLoading] = useState(false);

	/**
	 * Refresh the message limit status
	 */
	const refresh = useCallback(async () => {
		if (!userId) {
			setRemaining(null);
			setLimit(null);
			setIsLimited(false);
			return;
		}

		setLoading(true);
		try {
			const result = await canSendMessage(userId);

			// User has a limit if reason is "free_tier" or "limit_exceeded"
			const hasLimit = result.reason === "free_tier" || result.reason === "limit_exceeded";
			setIsLimited(hasLimit);

			if (hasLimit) {
				setLimit(result.limit || 20);
				setRemaining(result.remaining ?? 0);
			} else {
				setLimit(null);
				setRemaining(null);
			}
		} catch (error) {
			console.error("[useChatMessageLimit][refresh] - Error:", error);
			// Fail silently - don't show limit indicator on error
			setIsLimited(false);
			setLimit(null);
			setRemaining(null);
		} finally {
			setLoading(false);
		}
	}, [userId]);

	// Fetch limit status when dialog opens
	useEffect(() => {
		if (isOpen && userId) {
			refresh();
		}
	}, [isOpen, userId, refresh]);

	// Reset state when dialog closes
	useEffect(() => {
		if (!isOpen) {
			setRemaining(null);
			setLimit(null);
			setIsLimited(false);
		}
	}, [isOpen]);

	return {
		remaining,
		limit,
		isLimited,
		loading,
		refresh,
	};
};
